import { createTheme } from '@mui/material/styles';

// Automotive color palette
const automotiveColors = {
  petrolBlue: {
    50: '#f0f9ff',
    100: '#e0f2fe',
    200: '#bae6fd',
    300: '#7dd3fc',
    400: '#38bdf8',
    500: '#0ea5e9', // Main automotive blue
    600: '#0284c7', // Darker petrol blue
    700: '#0369a1',
    800: '#075985',
    900: '#0c4a6e',
  },
  metallicGrey: {
    50: '#f8fafc',
    100: '#f1f5f9',
    200: '#e2e8f0',
    300: '#cbd5e1',
    400: '#94a3b8',
    500: '#64748b', // Main metallic grey
    600: '#475569',
    700: '#334155',
    800: '#1e293b',
    900: '#0f172a',
  },
  chromeYellow: {
    50: '#fefce8',
    100: '#fef9c3',
    200: '#fef08a',
    300: '#fde047',
    400: '#facc15',
    500: '#eab308', // Chrome yellow for highlights
    600: '#ca8a04',
    700: '#a16207',
    800: '#854d0e',
    900: '#713f12',
  }
};

// Create a theme instance with automotive branding
export const theme = createTheme({
  palette: {
    primary: {
      main: automotiveColors.petrolBlue[600], // Petrol blue
      light: automotiveColors.petrolBlue[400],
      dark: automotiveColors.petrolBlue[800],
    },
    secondary: {
      main: automotiveColors.metallicGrey[500], // Metallic grey
      light: automotiveColors.metallicGrey[300],
      dark: automotiveColors.metallicGrey[700],
    },
    error: {
      main: '#d32f2f',
      light: '#ef5350',
      dark: '#c62828',
    },
    warning: {
      main: automotiveColors.chromeYellow[500], // Chrome yellow
      light: automotiveColors.chromeYellow[400],
      dark: automotiveColors.chromeYellow[600],
    },
    info: {
      main: '#0288d1',
      light: '#03a9f4',
      dark: '#01579b',
    },
    success: {
      main: '#2e7d32',
      light: '#4caf50',
      dark: '#1b5e20',
    },
    background: {
      default: '#f5f5f5',
      paper: '#ffffff',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 500,
    },
    h2: {
      fontWeight: 500,
    },
    h3: {
      fontWeight: 500,
    },
    h4: {
      fontWeight: 500,
    },
    h5: {
      fontWeight: 500,
    },
    h6: {
      fontWeight: 500,
    },
    button: {
      textTransform: 'none',
      fontWeight: 500,
    },
  },
  components: {
    // Global styles for smooth scrolling
    MuiCssBaseline: {
      styleOverrides: {
        html: {
          scrollBehavior: 'smooth',
        },
        body: {
          scrollBehavior: 'smooth',
        },
        // Apply to all scrollable elements
        '*': {
          '&[style*="overflow"]': {
            scrollBehavior: 'smooth',
          },
        },
        // Override auto-generated MUI Box classes
        '.MuiBox-root': {
          // Add global Box overrides here if needed
          // Example: Remove unwanted default styles
        },
        '.MuiBox-root.css-hlcs0k': {
          // Specific override for the css-hlcs0k class
          // Add your custom styles or use 'unset' to remove unwanted styles
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        rounded: {
          borderRadius: 12,
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: '0px 2px 4px -1px rgba(0,0,0,0.05), 0px 4px 5px 0px rgba(0,0,0,0.03), 0px 1px 10px 0px rgba(0,0,0,0.02)',
        },
      },
    },
  },
});

// Dark theme variant
export const darkTheme = createTheme({
  ...theme,
  palette: {
    ...theme.palette,
    mode: 'dark',
    primary: {
      main: automotiveColors.petrolBlue[400], // Brighter for dark mode
      light: automotiveColors.petrolBlue[300],
      dark: automotiveColors.petrolBlue[600],
    },
    secondary: {
      main: automotiveColors.metallicGrey[400], // Brighter for dark mode
      light: automotiveColors.metallicGrey[300],
      dark: automotiveColors.metallicGrey[600],
    },
    warning: {
      main: automotiveColors.chromeYellow[400], // Brighter for dark mode
      light: automotiveColors.chromeYellow[300],
      dark: automotiveColors.chromeYellow[600],
    },
    background: {
      default: '#121212',
      paper: '#1e1e1e',
    },
    text: {
      primary: '#ffffff',
      secondary: 'rgba(255, 255, 255, 0.8)', // Improved contrast from 0.7 to 0.8
    },
    divider: 'rgba(255, 255, 255, 0.2)',
    action: {
      hover: 'rgba(255, 255, 255, 0.04)',
      selected: 'rgba(255, 255, 255, 0.08)',
      disabled: 'rgba(255, 255, 255, 0.26)',
      disabledBackground: 'rgba(255, 255, 255, 0.12)',
    },
  },
  components: {
    ...theme.components,
    // Ensure smooth scrolling is inherited in dark theme
    MuiCssBaseline: {
      styleOverrides: {
        html: {
          scrollBehavior: 'smooth',
        },
        body: {
          scrollBehavior: 'smooth',
        },
        // Apply to all scrollable elements
        '*': {
          '&[style*="overflow"]': {
            scrollBehavior: 'smooth',
          },
        },
      },
    },
  },
});

export default theme;

